import React, { useState, useEffect } from "react";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';
import { createEmirate, updateEmirate, deleteEmirate } from '../../api/emirates';
import customFetch  from '../../hooks/fetchWrapper'
import ConfirmationDialog from "hooks/ConfirmationDialog";

const EmiratesTab = () => {

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [newEmirateName, setNewEmirateName] = useState('');

  useEffect(() => {
    fetchEmirates();
  }, []);

  const fetchEmirates = async () => {
    try {
      const data = await customFetch('emirates');
      setRows(data);
    } catch (error) {
      console.error('Error fetching emirates:', error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewEmirateName('');
  };

  const handleSubmitNewEmirate = async () => {
    if (newEmirateName.trim()) {
      try {
        const response = await createEmirate({ name: newEmirateName });
        if (response.status && response.data && response.data.length > 0) {
          const newEmirate = response.data[0];
          setRows(prevRows => [...prevRows, newEmirate]);
          handleCloseModal();
        } else {
          console.error('Unexpected response format:', response);
        }
      } catch (error) {
        console.error('Error creating emirate:', error);
      }
    }
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setSelectedRowId(id);
    setOpenConfirmDialog(true);
  };

  const handleDialogClose = (confirmed) => {
    setOpenConfirmDialog(false);
    if (confirmed) {
      handleConfirmDelete();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteEmirate(selectedRowId);
      setRows(rows.filter((row) => row.eid !== selectedRowId));
    } catch (error) {
      console.error('Error deleting area:', error);
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    updateEmirate(newRow.eid, updatedRow).then(() => {
      setRows(rows.map((row) => (row.eid === newRow.eid ? updatedRow : row)));
    });
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    { field: 'eid', headerName: 'EID', width: 100 },
    { field: 'name', headerName: 'Name',  flex: 1, minWidth: 150, editable: true },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
            icon={<Button variant="text" sx={{ minWidth: 20, padding: '0 0px' }}>Save</Button>}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
            icon={<Button variant="text" sx={{ minWidth: 40, padding: '0 0px' }}>Cancel</Button>}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ height: 500, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={handleOpenModal}>
          Add New Emirate
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        getRowId={(row) => row.eid} 
      />
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-emirate-modal"
        aria-describedby="modal-to-add-new-emirate"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
          <h2 id="add-emirate-modal">Add New Emirate</h2>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Emirate Name"
            type="text"
            fullWidth
            variant="standard"
            value={newEmirateName}
            onChange={(e) => setNewEmirateName(e.target.value)}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleSubmitNewEmirate} variant="contained">Submit</Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this Emirates? This action cannot be undone."
        onClose={handleDialogClose}
        onConfirm={() => handleDialogClose(true)}
      />
    </Box>
  );
};

export default EmiratesTab;